import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { type DeviceDetail as Detail, downloadWarrantyPdf, getDevice } from "../lib/api.ts";
import AlertsPanel from "../components/AlertsPanel.tsx";
import HistoryChart from "../components/HistoryChart.tsx";

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function DeviceDetail() {
  const { mac = "" } = useParams();
  const { t } = useTranslation();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const d = await getDevice(mac);
        if (!cancelled) setDetail(d);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mac]);

  if (error) return <p className="nx-error">{error}</p>;
  if (!detail) return <p>{t("device_detail.loading")}</p>;

  const isActive = detail.status === "active";

  return (
    <article className="nx-detail">
      <nav className="nx-breadcrumb">
        <Link to="/">{t("devices.label")}</Link>
        <span aria-hidden>/</span>
        <span className="nx-mono">{detail.mac}</span>
      </nav>

      <header className="nx-identity">
        <h1 className="nx-mono">{detail.mac}</h1>
        <span className={`nx-badge ${isActive ? "is-ok" : "is-bad"}`}>
          {detail.status.toUpperCase()}
        </span>
        {detail.unit_serial && (
          <button
            type="button"
            className="nx-btn-outline"
            onClick={() => {
              if (detail.unit_serial) {
                downloadWarrantyPdf(detail.unit_serial).catch((err) => alert(String(err)));
              }
            }}
            style={{ marginLeft: "auto" }}
          >
            {t("device_detail.warranty_pdf")}
          </button>
        )}
      </header>

      <section className="nx-kpi-strip">
        <div className="nx-kpi">
          <span className="nx-kpi-label">{t("device_detail.soc_bms")}</span>
          <span className="nx-kpi-value">
            {detail.soc_bms_pct !== null ? `${detail.soc_bms_pct}` : "—"}
            <small>%</small>
          </span>
        </div>
        <div className="nx-kpi">
          <span className="nx-kpi-label">{t("device_detail.voltage")}</span>
          <span className="nx-kpi-value">
            {detail.voltage_v !== null ? detail.voltage_v.toFixed(2) : "—"}
            <small>V</small>
          </span>
        </div>
        <div className="nx-kpi">
          <span className="nx-kpi-label">{t("device_detail.cycle_count")}</span>
          <span className="nx-kpi-value">{detail.cycle_count ?? "—"}</span>
        </div>
        <div className="nx-kpi">
          <span className="nx-kpi-label">{t("device_detail.last_seen")}</span>
          <span className="nx-kpi-value-small">{fmtDate(detail.last_seen_at)}</span>
        </div>
      </section>

      <HistoryChart mac={detail.mac} />
      <AlertsPanel mac={detail.mac} />
    </article>
  );
}
